import { enablePointerEvents } from '@utils/pointer-events-control';
/**
 * GameScene — City Builder coordinate plotting game.
 *
 * Flow:
 *   1. Tutorial dialog slides (kitty character explains the rules)
 *   2. 6 gameplay rounds, one per building. Player clicks the (x, y) on the
 *      grid; correct click animates the real building art into that cell.
 *   3. Win screen.
 *
 * The background is composed from modular art (mainBg base + graphBg tile
 * pattern + graph grid), with the palette frame and cards drawn on the left, so
 * this scene renders all dynamic elements directly: dialog text, palette
 * highlight, click hit-zone, placed-building sprites, feedback, and the win panel.
 */

import GameButton from '@components/GameButton';
import ProgressBar from '@components/ProgressBar';
import TextBox from '@components/TextBox';
import { SFX, playSfx, startBgMusic } from '@utils/SoundFX';

const ROUNDS = [
  { key: 'building-house', name: 'House', x: 2, y: -3 },
  { key: 'building-mall', name: 'Mall', x: -7, y: -6 },
  { key: 'building-cafe', name: 'Cafe', x: 0, y: 3 },
  { key: 'building-hospital', name: 'Hospital', x: 6, y: 5 },
  { key: 'building-hotel', name: 'Hotel', x: -9, y: 5 },
  { key: 'building-supermarket', name: 'Supermarket', x: 8, y: -6 },
];

// The grid art (graphBg + graph) is drawn unscaled centered at this point.
const GRID_ART_CENTER = { x: 807, y: 433 };

// Pixel anchors for the coordinate math. graph.png (919×556) is centered at
// GRID_ART_CENTER; measuring its gridlines puts (0,0) at image pixel (460, 282)
// — offset (+0.5, +4) from the image center — with 35px between lines. So the
// scene location of coordinate (0,0) is (807.5, 437), distinct from the art's
// center above.
const GRID = {
  originX: GRID_ART_CENTER.x + 0.5,
  originY: GRID_ART_CENTER.y + 4,
  cellSize: 35,
  xMin: -12,
  xMax: 12,
  yMin: -7,
  yMax: 7,
  // Click must land within this many px of an intersection to count.
  hitRadius: 14,
};

// Left-side palette: imported card frames (139×173 each) tiled 2 columns ×
// 3 rows. The grid (278×519) is centered at (176, 440), matching the footprint
// the old buildingSelection.png occupied. Each slot pairs a card frame with the
// matching building sprite, drawn into the card's white image area.
const PALETTE_SLOTS = [
  { key: 'building-mall', card: 'mallCard' },
  { key: 'building-house', card: 'houseCard' },
  { key: 'building-cafe', card: 'cafeCard' },
  { key: 'building-hospital', card: 'hospitalCard' },
  { key: 'building-hotel', card: 'hotelCard' },
  { key: 'building-supermarket', card: 'supermarketCard' },
];
const PALETTE = {
  cardW: 139,
  cardH: 173,
  centerX: 176,
  centerY: 440,
  // Spacing between card centers. Set below cardW/cardH so the cards sit
  // closer together (borders nearly touching) inside the green panel.
  colSpacing: 126,
  rowSpacing: 168,
  // Building sprite sits in the card's white area, above the name strip.
  iconOffsetY: -14,
  iconFit: 100,
};

// TEMP (testing): which round to start on after the tutorial. Set to
// ROUNDS.length - 1 to jump straight to the last building and reach the
// completion sequence quickly. Revert to 0 for the normal full game.
const START_ROUND = 0;

const TUTORIAL_SLIDES = ["Let's learn how to play the game!"];

export default class GameScene extends Phaser.Scene {
  constructor() {
    super({ key: 'GameScene' });
  }

  init(data) {
    this.gameState = data || {};
    this.mode = 'tutorial';
    this.tutorialIndex = 0;
    this.roundIndex = 0;
    this.isAcceptingInput = false;
    this.placedBuildings = [];
    // Placed building sprite keyed by building key, so the completion sequence
    // can hide the one on the graph when its city animation plays.
    this.placedByKey = {};
    // Keys of buildings already placed; their cards stay highlighted (in color)
    // for the rest of the game.
    this.completedKeys = new Set();
    // Heavy end-of-game assets are warmed up in the background during play
    // (see preloadCompletionAssets) so the final animation doesn't stutter.
    this.completionAssetsReady = false;
    this.pendingCompletion = false;
  }

  preload() {}

  create() {
    // Enable pointer events so hover states and the cursor work
    enablePointerEvents();

    // Layered background composed from imported modular assets:
    //   mainBg  — full-screen light-green base
    //   graphBg — isometric tile pattern inside the grid area
    //   graph   — coordinate grid with axes, labels, and arrows
    // The art is centered at GRID_ART_CENTER; the coordinate (0,0) used by
    // pointerToPoint() below sits at GRID.originX / GRID.originY, slightly
    // offset from that center (see the GRID comment).
    this.add.image(640, 360, 'mainBg').setDisplaySize(1280, 720).setDepth(-3);
    this.graphBgImg = this.add
      .image(GRID_ART_CENTER.x, GRID_ART_CENTER.y, 'graphBg')
      .setDepth(-2);
    this.graphImg = this.add
      .image(GRID_ART_CENTER.x, GRID_ART_CENTER.y, 'graph')
      .setDepth(-1);
    this.createPalette();

    // Full-width header strip across the top; the dialog box sits on top of it.
    // Header depths sit above the completion animation (depth 6) so the strip,
    // progress tracker and dialog stay in front of it.
    this.add.image(640, 0, 'top-overlay').setOrigin(0.5, 0).setDepth(15);

    // Step tracker (one dot per building round), centered in the top strip.
    this.progressBar = new ProgressBar(this, 640, 21);
    this.progressBar.setDepth(16);

    this.scene.launch('UIScene');

    this.createDialogOverlay();
    this.createGridHitZone();
    this.setupConfetti();

    // Quiet, looping town theme under the whole game. Stored on the global game
    // so it carries across scenes and never stacks a second loop on replay.
    startBgMusic(this);

    this.startTutorial();

    // Warm up the heavy completion assets in the background while the kid plays,
    // so they're already decoded by the time the final animation starts.
    this.preloadCompletionAssets();
  }

  // Queue the heavy end-of-game assets (the combined city-build animation and
  // the cinematic EndScene art) and load them in the background during play.
  // playCompletionSequence() just waits on completionAssetsReady afterwards.
  preloadCompletionAssets() {
    let needLoad = false;
    if (!this.textures.exists('cityBuildingAnim')) {
      const base = 'assets/GameAssets/citybuilding';
      this.load.multiatlas('cityBuildingAnim', `${base}/cityBuildingAnim.json`, base);
      needLoad = true;
    }
    if (!this.textures.exists('endScreenBG')) {
      this.queueEndSceneAssets();
      needLoad = true;
    }
    if (!needLoad) {
      this.completionAssetsReady = true;
      return;
    }
    this.load.once('complete', () => {
      this.completionAssetsReady = true;
      // If the player already finished while assets were still loading, the
      // completion sequence is waiting — kick it off now.
      if (this.pendingCompletion) {
        this.pendingCompletion = false;
        this.playCityBuilding();
      }
    });
    this.load.start();
  }

  setupConfetti() {
    if (!this.anims.exists('confetti-burst')) {
      this.anims.create({
        key: 'confetti-burst',
        frames: this.anims.generateFrameNames('confetti', {
          start: 0,
          end: 47,
          zeroPad: 0,
          prefix: 'checkconfetti',
          suffix: '.png',
        }),
        frameRate: 25,
        repeat: 0,
      });
    }
  }

  playConfetti(x, y) {
    const burst = this.add.sprite(x, y, 'confetti').setDepth(6).setScale(1.2);
    burst.play('confetti-burst');
    burst.once('animationcomplete', () => burst.destroy());
  }

  // Completion: fade out the HUD (character/dialog + building palette) over
  // ~700ms, then start the city build-up.
  playCompletionSequence() {
    this.isAcceptingInput = false;
    this.fadeOutHud(() => this.beginCityBuild());
  }

  // Fade out the dialog box (character + text) and the building-selection
  // palette (cards + green frame) together over ~700ms, then run onComplete.
  fadeOutHud(onComplete) {
    const fading = [this.dialogBox, this.paletteFrame].filter(Boolean);
    Object.values(this.paletteItems || {}).forEach(item => {
      fading.push(item.card, item.icon);
    });
    this.tweens.add({
      targets: fading,
      alpha: 0,
      duration: 700,
      ease: 'Sine.easeInOut',
      onComplete: () => {
        fading.forEach(o => o.setVisible(false));
        onComplete();
      },
    });
  }

  // Start the city build-up once the heavy assets are ready (they're warmed up
  // in the background during play). If still loading, flag it to start on
  // completion (see preloadCompletionAssets).
  beginCityBuild() {
    if (this.completionAssetsReady) {
      this.playCityBuilding();
    } else {
      this.pendingCompletion = true;
    }
  }

  // Queue the (cinematic) EndScene assets, copied from balance-out. endScreenBG
  // and the reward icon are placeholders — replace those PNGs with the
  // city-specific background and reward art.
  queueEndSceneAssets() {
    const base = 'assets/EndSceneAssets';
    this.load.multiatlas(
      'spotlight',
      `${base}/spotlight/spotlightAnimNew.json`,
      `${base}/spotlight`,
    );
    this.load.multiatlas(
      'endConfetti',
      `${base}/confetti/confettiAnimNew.json`,
      `${base}/confetti`,
    );
    this.load.multiatlas('rewardBg', `${base}/rewardBg.json`, base);
    this.load.image('endScreenBG', `${base}/endSceenBG.png`);
    this.load.image('youWon', `${base}/You won.png`);
    this.load.image('endNextButton', `${base}/next button.png`);
    this.load.image('nextHover', `${base}/next hover.png`);
    this.load.image('playAgainButton', `${base}/playAgainButton.png`);
    this.load.image('playAgainButtonHover', `${base}/playAgainButton-hover.png`);
    this.load.image('rewardEarned', `${base}/Rewards earned!.png`);
    this.load.image('rewardName', `${base}/constructionCap-label.png`);
    this.load.image('rewardIcon', `${base}/constructionCap.png`);
  }

  // The combined animation is split into three segments (frames are
  // cityBuildingAnim0..119, not zero-padded, with 109 missing):
  //   seg1  0–38   city + buildings pop
  //   seg2  39–63  trees pop
  //   seg3  64–119 cars drive in (last frame held)
  playCityBuilding() {
    this.createCitySegment('city-seg1', 0, 38);
    this.createCitySegment('city-seg2', 39, 63);
    this.createCitySegment('city-seg3', 64, 119);

    // Create the city animation aligned over the grid ground, but keep it hidden
    // until the frame has slid to the centre — the building pop plays only there.
    this.cityAnimSprite = this.add
      .sprite(640, 360, 'cityBuildingAnim', 'cityBuildingAnim0.png')
      .setDepth(6)
      .setVisible(false);

    this.slideFrameToCentre();
  }

  // Step 1: slide the whole coordinate frame (grid art + ground + placed
  // buildings) and the still-hidden city animation to the screen centre.
  // Nothing animates yet — the buildings just travel to the middle.
  slideFrameToCentre() {
    const dx = 640 - this.graphBgImg.x;
    const dy = 360 + 40 - this.graphBgImg.y;
    const targets = [
      this.graphBgImg,
      this.graphImg,
      this.cityAnimSprite,
      ...this.placedBuildings,
    ].filter(Boolean);
    this.tweens.add({
      targets,
      x: `+=${dx}`,
      y: `+=${dy}`,
      duration: 600,
      ease: 'Sine.easeInOut',
      onComplete: () => this.onFrameCentred(),
    });
  }

  // Step 2: frame is centred — hide the grid lines and placed buildings, reveal
  // the city animation in their place and pop the buildings in (they grow). The
  // animation stops here once the pop finishes.
  onFrameCentred() {
    this.graphImg?.setVisible(false);
    this.hoverHighlight?.setVisible(false);
    this.placedBuildings.forEach(b => b.setVisible(false));
    this.cityAnimSprite.setVisible(true);
    // The whole skyline pops in here.
    playSfx(this, SFX.popping);
    this.cityAnimSprite.play('city-seg1');
    this.cityAnimSprite.once('animationcomplete', () => this.onBuildingsPopped());
  }

  createCitySegment(key, from, to) {
    if (this.anims.exists(key)) return;
    const frames = [];
    for (let f = from; f <= to; f++) {
      if (f === 109) continue; // missing frame
      frames.push(
        ...this.anims.generateFrameNames('cityBuildingAnim', {
          prefix: 'cityBuildingAnim',
          suffix: '.png',
          start: f,
          end: f,
        }),
      );
    }
    this.anims.create({ key, frames, frameRate: 24, repeat: 0 });
  }

  // Step 3: buildings popped — pause, then zoom in. Once the zoom settles, swap
  // the grid ground for the city map and play the rest of the animation.
  onBuildingsPopped() {
    this.zoomCityToFill(() => {
      this.transitionToCityBg(() => this.playRest());
    });
  }

  // Step 4: trees pop, then cars drive in. When the last frame is reached, hand
  // off to the cinematic end screen.
  playRest() {
    // Trees pop in across the city.
    playSfx(this, SFX.pop);
    this.cityAnimSprite.play('city-seg2');
    this.cityAnimSprite.once('animationcomplete', () => {
      this.cityAnimSprite.play('city-seg3');
      this.cityAnimSprite.once('animationcomplete', () => this.scene.launch('EndScene'));
    });
  }

  // Scale the city (animation + grounds) about the screen centre so it fills the
  // view, then run onComplete. Position is tweened alongside scale so each grows
  // about (640, 360).
  zoomCityToFill(onComplete) {
    const scale = 1.45;
    const px = 640;
    const py = 360;
    const duration = 1800;
    // Camera pushes in on the finished city.
    playSfx(this, SFX.zoom);
    [this.cityAnimSprite, this.cityBgImg, this.graphBgImg]
      .filter(Boolean)
      .forEach(obj => {
        this.tweens.add({
          targets: obj,
          scaleX: scale,
          scaleY: scale,
          x: px + (obj.x - px) * scale,
          y: py + (obj.y - py) * scale,
          duration,
          ease: 'Sine.easeInOut',
        });
      });
    if (onComplete) this.time.delayedCall(duration, onComplete);
  }

  // Crossfade the grid tile background into the city ground map (centred where
  // graphBg now sits), then run onComplete. CITY_BG_ALIGN_UP nudges cityBg up
  // so its roads/plots sit correctly under the animated buildings.
  transitionToCityBg(onComplete) {
    const CITY_BG_ALIGN_UP = 25;
    // Match graphBg's current scale/position so cityBg lines up even though the
    // crossfade now runs after the zoom (graphBg is scaled by then).
    const s = this.graphBgImg.scaleX;
    this.cityBgImg = this.add
      .image(this.graphBgImg.x, this.graphBgImg.y - CITY_BG_ALIGN_UP * s, 'cityBg')
      .setScale(s)
      .setDepth(-2)
      .setAlpha(0);
    this.tweens.add({ targets: this.cityBgImg, alpha: 1, duration: 600, onComplete });
    this.tweens.add({ targets: this.graphBgImg, alpha: 0, duration: 600 });
  }

  update() {}

  // ==================== DIALOG ====================

  createDialogOverlay() {
    // Box uses origin (0, 0.5) — its local (0,0) is the LEFT-CENTER of the
    // pill, planted at the character's anchor point (x=84, y=92). The
    // character image is shifted -20px up inside the container so it sits
    // above the pill's vertical center (peeking out above the pill). Flat
    // left corners let the character circle provide the rounding on that
    // side; right end is fully rounded.
    this.dialogBox = new TextBox(this, 64, 96, {
      character: 'character',
      characterPosition: { x: 20, y: -10 },
      origin: { x: 0, y: 0.5 },
      textOrigin: { x: 0, y: 0.5 },
      textOffset: { x: 110, y: 0 },
      fixedWidth: 952,
      fixedHeight: 104,
      backgroundColor: 0xffffff,
      backgroundAlpha: 1,
      borderColor: 0x1f2d2d,
      borderWidth: 2.5,
      borderRadius: { tl: 0, tr: 50, bl: 0, br: 50 },
      paddingHorizontal: 0,
      paddingVertical: 0,
      textColor: '#2B2B2B',
      fontSize: '26px',
      fontFamily: 'Athletics-Medium',
      wordWrapWidth: 816,
      textAlign: 'left',
      animationDuration: 250,
    });
    this.dialogBox.setDepth(20);
  }

  setDialog(text) {
    this.dialogBox.showText(text);
  }

  // ==================== BUILDING PALETTE (left box) ====================

  // Tile the imported card frames into the left box and drop each building
  // sprite into its card. The active round's card stays in full color while the
  // rest are desaturated, so the player can see which building they're placing.
  createPalette() {
    const { centerX, centerY, colSpacing, rowSpacing, iconOffsetY, iconFit } = PALETTE;

    this.paletteItems = {};
    PALETTE_SLOTS.forEach((slot, i) => {
      const col = i % 2;
      const row = Math.floor(i / 2);
      // 2 columns centered on centerX; 3 rows centered on centerY.
      // Nudge the left column (mall, cafe, hotel) 7px further left.
      const cx = centerX + (col - 0.5) * colSpacing - (col === 0 ? 7 : 0);
      const cy = centerY + (row - 1) * rowSpacing;

      const card = this.add.image(cx, cy, slot.card).setDepth(-2);
      const icon = this.add.image(cx, cy + iconOffsetY, slot.key).setDepth(-2);
      icon.setScale(Math.min(iconFit / icon.width, iconFit / icon.height));

      this.paletteItems[slot.key] = {
        card,
        icon,
        cardFx: card.postFX.addColorMatrix(),
        iconFx: icon.postFX.addColorMatrix(),
      };
    });

    // Green frame drawn in front of the cards; its transparent center lets the
    // cards show through while the border frames the whole grid. Shifted 2px
    // left to sit centered over the nudged card grid.
    this.paletteFrame = this.add
      .image(centerX - 2, centerY, 'greenRectangle')
      .setDepth(-1);

    // Start with every card greyscale; startRound highlights the active one
    // (after a short delay) once the place instruction is shown.
    this.updatePaletteHighlight(null);
  }

  updatePaletteHighlight(activeKey) {
    if (!this.paletteItems) return;
    Object.entries(this.paletteItems).forEach(([key, item]) => {
      // The active building and any already-placed buildings stay in color;
      // the rest are greyscale.
      const lit = key === activeKey || this.completedKeys.has(key);
      // reset() returns the matrix to identity (full color); grayscale(0) is
      // treated as 1 by Phaser, so we reset rather than pass 0.
      item.cardFx.reset();
      item.iconFx.reset();
      if (!lit) {
        item.cardFx.grayscale();
        item.iconFx.grayscale();
      }

      // Active card sits in front of the other (overlapping) cards, but still
      // behind the green frame at -1.
      const depth = key === activeKey ? -1.5 : -2;
      item.card.setDepth(depth);
      item.icon.setDepth(depth);
    });
  }

  // ==================== GRID HIT ZONE ====================

  createGridHitZone() {
    const { originX, originY, cellSize, xMin, xMax, yMin, yMax, hitRadius } = GRID;
    const left = originX + (xMin - 0.5) * cellSize;
    const top = originY + (yMin - 0.5) * cellSize;
    const w = (xMax - xMin + 1) * cellSize;
    const h = (yMax - yMin + 1) * cellSize;

    // No default cursor on the zone itself — the cursor only turns into a
    // pointer when the mouse is actually near a coordinate (see pointermove).
    this.hitZone = this.add
      .rectangle(left + w / 2, top + h / 2, w, h, 0x000000, 0.001)
      .setInteractive()
      .setDepth(2);

    // Hover highlight: a marker that snaps to the nearest grid intersection as
    // the pointer moves, so the player can preview which coordinate a click
    // will select (mirrors the cell highlight in lets-start-mining). Sits above
    // the grid art but below the hit zone and placed buildings.
    this.hoverHighlight = this.add
      .circle(0, 0, cellSize * 0.3, 0xffffff, 0.3)
      .setStrokeStyle(2, 0x1f6e3a, 0.9)
      .setDepth(1)
      .setVisible(false);

    this.hitZone.on('pointermove', pointer => {
      if (!this.isAcceptingInput) {
        this.clearGridHover();
        return;
      }
      const point = this.pointerToPoint(pointer);
      const px = originX + point.x * cellSize;
      const py = originY - point.y * cellSize;
      // Only highlight / show the pointer cursor when the mouse is within
      // hitRadius of the intersection — not in the gaps between coordinates.
      const near =
        Phaser.Math.Distance.Between(pointer.worldX, pointer.worldY, px, py) <= hitRadius;
      this.hoverHighlight.setPosition(px, py).setVisible(near);
      this.input.setDefaultCursor(near ? 'pointer' : 'default');
    });

    this.hitZone.on('pointerout', () => this.clearGridHover());

    this.hitZone.on('pointerup', pointer => {
      if (!this.isAcceptingInput) return;
      this.clearGridHover();
      const point = this.pointerToPoint(pointer);
      this.handleCellClick(point.x, point.y);
    });
  }

  // Hide the hover marker and reset the cursor to the default arrow.
  clearGridHover() {
    this.hoverHighlight?.setVisible(false);
    this.input.setDefaultCursor('default');
  }

  // Snap the pointer to the nearest integer coordinate, clamped to the grid
  // bounds. Always returns a point — anywhere the player clicks resolves to the
  // closest coordinate.
  pointerToPoint(pointer) {
    const { originX, originY, cellSize, xMin, xMax, yMin, yMax } = GRID;
    const cellX = Math.round((pointer.worldX - originX) / cellSize);
    const cellY = -Math.round((pointer.worldY - originY) / cellSize);
    return {
      x: Phaser.Math.Clamp(cellX, xMin, xMax),
      y: Phaser.Math.Clamp(cellY, yMin, yMax),
    };
  }

  // ==================== TUTORIAL ====================

  startTutorial() {
    this.mode = 'tutorial';
    this.tutorialIndex = 0;
    this.showTutorialSlide();
  }

  showTutorialSlide() {
    this.setDialog(TUTORIAL_SLIDES[this.tutorialIndex]);
    this.tutorialTimer?.remove();
    this.tutorialTimer = this.time.delayedCall(3500, () => this.advanceTutorial());
  }

  advanceTutorial() {
    this.tutorialIndex++;
    if (this.tutorialIndex < TUTORIAL_SLIDES.length) {
      this.showTutorialSlide();
      return;
    }
    this.endTutorial();
  }

  endTutorial() {
    this.mode = 'gameplay';
    this.startRound(START_ROUND);
  }

  // Animated hand cursor that points at a grid coordinate and taps it in a
  // loop, so the player can see exactly where to click during the tutorial.
  showTutorialHand(cellX, cellY) {
    const { originX, originY, cellSize } = GRID;
    const px = originX + cellX * cellSize;
    const py = originY - cellY * cellSize;

    // Black dot marking the exact target coordinate the hand points at.
    this.tutorialDot = this.add.circle(px, py, 5, 0x000000, 1).setDepth(5);

    // Origin (0.27, 0.13) is the fingertip pixel (31, 18) in the 116×132 hand
    // image, so the tip lands exactly on the target point; the body trails
    // down-right of it.
    this.tutorialHand = this.add
      .image(px, py, 'hand')
      .setOrigin(0.27, 0.13)
      .setScale(0.5)
      .setDepth(6);

    // Tap loop: nudge the fingertip down onto the point and back up.
    this.tutorialHandTween = this.tweens.add({
      targets: this.tutorialHand,
      y: py + 10,
      scale: 0.46,
      duration: 480,
      ease: 'Sine.easeInOut',
      yoyo: true,
      repeat: -1,
      hold: 120,
    });
  }

  hideTutorialHand() {
    this.tutorialHandTween?.remove();
    this.tutorialHandTween = null;
    this.tutorialHand?.destroy();
    this.tutorialHand = null;
    this.tutorialDot?.destroy();
    this.tutorialDot = null;
  }

  // ==================== ROUND FLOW ====================

  startRound(index) {
    if (index >= ROUNDS.length) {
      // All buildings placed: play the city "comes to life" sequence.
      this.playCompletionSequence();
      return;
    }
    this.roundIndex = index;
    const round = ROUNDS[index];
    this.progressBar.setStep(index);
    this.setDialog(`Place '${round.name}' at (${round.x}, ${round.y}).`);
    this.isAcceptingInput = true;

    // Highlight the target card a beat after the instruction is given (rather
    // than instantly). Guard against stale firings if the round has moved on.
    this.highlightTimer?.remove();
    this.highlightTimer = this.time.delayedCall(800, () => {
      if (this.roundIndex === index) this.updatePaletteHighlight(round.key);
    });

    // Tutorial round (the first building): point a hand cursor at the target
    // coordinate so the player sees exactly where to tap.
    this.hideTutorialHand();
    if (index === 0) {
      this.showTutorialHand(round.x, round.y);
    }
  }

  handleCellClick(cellX, cellY) {
    const round = ROUNDS[this.roundIndex];
    const correct = cellX === round.x && cellY === round.y;

    this.isAcceptingInput = false;
    this.hideTutorialHand();
    if (correct) {
      this.flyBuildingFromCard(round, cellX, cellY);
    } else {
      this.showWrongDot(round, cellX, cellY);
    }
  }

  // Correct answer: animate the building flying out of its palette card to the
  // clicked grid point, shrinking to its placed size, then celebrate.
  flyBuildingFromCard(round, cellX, cellY) {
    const { originX, originY, cellSize } = GRID;
    const destX = originX + cellX * cellSize;
    const destY = originY - cellY * cellSize;
    const restY = destY + 4 + (round.dy ?? 0);

    // Whoosh as the building lifts off its card and flies to the grid.
    playSfx(this, SFX.appear);

    const source = this.paletteItems[round.key].icon;
    const sprite = this.add
      .image(source.x, source.y, round.key)
      .setOrigin(0.5, 0.6)
      .setScale(source.scaleX)
      .setDepth(4);

    // The building leaves its card for good, so hide the card's own icon as the
    // flying copy lifts off — the card reads as empty.
    source.setVisible(false);

    this.tweens.add({
      targets: sprite,
      x: destX,
      y: restY,
      scale: 0.55,
      ease: 'Sine.easeInOut',
      duration: 480,
      onComplete: () => this.onPlaceCorrect(sprite, round, destX, destY),
    });
  }

  // Wrong answer: no building animation — just pop a red dot at the clicked
  // coordinate as feedback, then fade it and let the player try again.
  showWrongDot(round, cellX, cellY) {
    const { originX, originY, cellSize } = GRID;
    const px = originX + cellX * cellSize;
    const py = originY - cellY * cellSize;

    playSfx(this, SFX.error);

    const dot = this.add
      .circle(px, py, cellSize * 0.3, 0xe23b3b, 1)
      .setStrokeStyle(2, 0xffffff, 0.9)
      .setDepth(4)
      .setScale(0);
    this.tweens.add({ targets: dot, scale: 1, duration: 150, ease: 'Back.easeOut' });

    this.time.delayedCall(900, () => {
      this.tweens.add({
        targets: dot,
        alpha: 0,
        duration: 250,
        onComplete: () => dot.destroy(),
      });
      // Let the player try again (re-show the tutorial hand on round 0).
      this.isAcceptingInput = true;
      if (this.roundIndex === 0) {
        this.showTutorialHand(round.x, round.y);
      }
    });
  }

  onPlaceCorrect(sprite, round, x, y) {
    this.placedBuildings.push(sprite);
    this.placedByKey[round.key] = sprite;
    this.completedKeys.add(round.key);
    // Success chime as the building settles and the confetti bursts.
    playSfx(this, SFX.correct);
    this.playConfetti(x, y);
    // Last building placed correctly — celebrate in the dialog box and move on
    // to the completion sequence sooner than the normal between-round pause.
    const isLast = this.roundIndex === ROUNDS.length - 1;
    if (isLast) {
      this.setDialog('Yeayy!');
    }
    this.time.delayedCall(isLast ? 800 : 2000, () =>
      this.startRound(this.roundIndex + 1),
    );
  }

  // ==================== WIN ====================

  showWinScreen() {
    this.isAcceptingInput = false;

    const overlay = this.add.rectangle(640, 360, 1280, 720, 0x12321f, 0.72).setDepth(10);

    const panel = this.add
      .rectangle(640, 360, 540, 320, 0xffffff, 1)
      .setStrokeStyle(4, 0x1f6e3a)
      .setDepth(11);

    const title = this.add
      .text(640, 268, 'CITY COMPLETE!', {
        fontFamily: 'Athletics-Black',
        fontSize: '44px',
        color: '#1f6e3a',
      })
      .setOrigin(0.5)
      .setDepth(12);

    const subtitle = this.add
      .text(640, 332, 'You plotted all 6 buildings on the grid.', {
        fontFamily: 'Athletics-Medium',
        fontSize: '20px',
        color: '#1f2d2d',
      })
      .setOrigin(0.5)
      .setDepth(12);

    const replay = new GameButton(this, 640, 420, 'replayButton', {
      hoverScale: 1.08,
      pressScale: 0.92,
      tweenDuration: 120,
    })
      .setHoverTexture('hover_replayButton')
      .onClick(() => {
        this.scene.stop('GameScene');
        this.scene.start('StartScene');
      });
    replay.setDepth(12);

    [overlay, panel, title, subtitle, replay].forEach(o => {
      o.setAlpha(0);
      this.tweens.add({ targets: o, alpha: 1, duration: 300 });
    });
  }
}
